import { useEffect, useRef, useState } from "react";
import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import HomeIcon from "@mui/icons-material/Home";
import LoginIcon from "@mui/icons-material/Login";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PrintIcon from "@mui/icons-material/Print";
import RateReviewIcon from "@mui/icons-material/RateReview";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ScoreCircle from "../ScoreCircle";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createAxios } from "../../createAxios";
import { getUserId } from "../../utils/authSession";
import CONSTANTS from "../../constants";
import { getAnswer, getHistory, logout } from "../../redux/apiRequest";
import CommentSection from "../CommentSection";

const QUESTION_GROUPS = [
  { title: "Listening Part 1", range: "Questions 1-6", start: 0, end: 6, icon: HeadphonesIcon },
  { title: "Listening Part 2", range: "Questions 7-31", start: 6, end: 31, icon: HeadphonesIcon },
  { title: "Listening Part 3", range: "Questions 32-70", start: 31, end: 70, icon: HeadphonesIcon },
  { title: "Listening Part 4", range: "Questions 71-100", start: 70, end: 100, icon: HeadphonesIcon },
  { title: "Reading Part 5", range: "Questions 101-130", start: 100, end: 130, icon: MenuBookIcon },
  { title: "Reading Part 6", range: "Questions 131-146", start: 130, end: 146, icon: MenuBookIcon },
  { title: "Reading Part 7", range: "Questions 147-200", start: 146, end: 200, icon: MenuBookIcon },
];

function Review() {
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { historyID, answerID } = useParams();
  const [answers, setAnswers] = useState(Array(200).fill(""));
  const [name, setName] = useState("");
  const [audio, setAudio] = useState("");
  const [trueAnswers, setTrueAnswers] = useState("");
  const [listeningTrueAnswers, setListeningTrueAnswers] = useState(0);
  const [readingTrueAnswers, setReadingTrueAnswers] = useState(0);
  const audioRef = useRef(null);
  const { listeningScores, readingScores } = CONSTANTS;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const avatar = currentUser?.metadata.user.user_avatar;
  const userID = getUserId(currentUser);
  const accessToken = currentUser?.metadata.tokens.accessToken;
  const axiosJWT = createAxios(currentUser);

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const listeningScore =
    listeningScores[listeningTrueAnswers - 1 >= 0 ? listeningTrueAnswers - 1 : 0];
  const readingScore =
    readingScores[readingTrueAnswers - 1 >= 0 ? readingTrueAnswers - 1 : 0];
  const totalScore = listeningScore + readingScore;
  const correctCount = listeningTrueAnswers + readingTrueAnswers;
  const wrongCount = 200 - correctCount;

  const handleLogout = () => {
    logout(accessToken, userID, dispatch, navigate, axiosJWT);
  };

  const settings = [
    { title: "Profile", href: userID ? `/profile/${userID}` : "" },
    { title: "Account", href: userID ? `/account/${userID}` : "" },
    { title: "Logout", href: "", onClick: handleLogout },
  ];
  const sessions = [
    { title: "Sign in", href: "/signin" },
    { title: "Sign up", href: "/signup" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const data = await getAnswer(answerID, dispatch);
      const history = await getHistory(userID, answerID, dispatch);
      const answersData = data.content.replace(/, |\s+/g, "");
      const historyData = history.answer.replace(/, |\s+/g, "");
      let listenCount = 0;
      let readCount = 0;

      for (let i = 0; i < 200; i += 1) {
        if (answersData[i] === historyData[i]) {
          if (i < 100) listenCount += 1;
          else readCount += 1;
        }
      }

      setListeningTrueAnswers(listenCount);
      setReadingTrueAnswers(readCount);
      setTrueAnswers(answersData);
      setAnswers(historyData);
      setName(data.name);
      setAudio(data.audio);
    };

    if (userID) fetchData();
  }, [dispatch, historyID, answerID, userID]);

  const getQuestionState = (index, option) => {
    const correctAnswer = trueAnswers[index];
    const selectedAnswer = answers[index];

    if (option === correctAnswer) {
      return { color: "#35c759", bgcolor: "#e8f9ed", borderColor: "#35c759" };
    }
    if (selectedAnswer === option && selectedAnswer !== correctAnswer) {
      return { color: "#d93025", bgcolor: "#fff1f1", borderColor: "#d93025" };
    }
    return {};
  };

  const renderResultChip = (index) => {
    if (answers[index] === trueAnswers[index]) {
      return (
        <Chip
          size="small"
          icon={<CheckCircleIcon />}
          label="Correct"
          sx={{ bgcolor: "#e8f9ed", color: "#1f9b45", "& .MuiChip-icon": { color: "#1f9b45" } }}
        />
      );
    }

    return (
      <Chip
        size="small"
        icon={<CloseIcon />}
        label={`Answer ${trueAnswers[index] || "-"}`}
        sx={{ bgcolor: "#fff1f1", color: "#d93025", "& .MuiChip-icon": { color: "#d93025" } }}
      />
    );
  };

  const renderQuestions = (start, end) => {
    const questions = [];

    for (let index = start; index < end; index += 1) {
      questions.push(
        <Paper
          elevation={0}
          key={`question-${index}`}
          sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e5edf5", bgcolor: "#ffffff" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
            <Typography sx={{ color: "#07111f", fontWeight: 700 }}>Q{index + 1}</Typography>
            {renderResultChip(index)}
          </Box>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              row
              aria-label={`group${index}`}
              name={`group${index}`}
              value={answers[index] || ""}
              sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 0.75 }}
            >
              {["A", "B", "C", "D"].map((option) => {
                const state = getQuestionState(index, option);

                return (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={
                      <Radio
                        size="small"
                        readOnly
                        sx={{ p: 0.5, color: "#9aa8b8", "&.Mui-checked": { color: state.color || "#2196f3" } }}
                      />
                    }
                    label={option}
                    sx={{
                      m: 0,
                      px: 0.75,
                      py: 0.5,
                      borderRadius: 1.5,
                      border: `1px solid ${state.borderColor || "#e5edf5"}`,
                      bgcolor: state.bgcolor || "#f5f8fb",
                      color: state.color || "#07111f",
                      ".MuiFormControlLabel-label": { fontSize: 14 },
                    }}
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
        </Paper>
      );
    }

    return questions;
  };

  const handlePrintResult = () => {
    const element = document.body;

    html2canvas(element, { useCORS: true })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save(`${name} ${totalScore}.pdf`);
      })
      .catch(() => {
        alert("This function supports only Chrome, Firefox, Safari and Edge browsers, sorry!");
      });
  };

  const handleJumpToPart = (partIndex) => {
    document
      .getElementById(`review-part-${partIndex + 1}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box sx={{ bgcolor: "#f5f8fb", minHeight: "100vh", pb: 6 }}>
      <Box sx={{ position: "sticky", top: 0, zIndex: 20, bgcolor: "#ffffff", borderBottom: "1px solid #e5edf5" }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              minHeight: 72,
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "auto minmax(260px, 1fr) auto auto",
              alignItems: "center",
              gap: 2,
              py: 1.25,
            }}
          >
            <Button component={Link} to="/" startIcon={<HomeIcon />} sx={{ justifySelf: isMobile ? "start" : "auto", color: "#07111f" }}>
              Home
            </Button>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, overflow: "hidden" }}>
              <Box sx={{ width: 42, height: 42, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "#ffe8f0", color: "#d81b60", flex: "0 0 auto" }}>
                <RateReviewIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: "#07111f", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  Review {name || "TOEIC Practice Test"}
                </Typography>
                <Typography sx={{ color: "#5f6b7a", fontSize: 13 }}>
                  {correctCount}/200 correct
                </Typography>
              </Box>
            </Box>

            {audio && (
              <audio
                style={{ width: isMobile ? "100%" : "280px", maxWidth: "100%", gridColumn: isMobile ? "1 / -1" : "auto" }}
                src={audio}
                controls
                ref={audioRef}
              />
            )}

            {!currentUser ? (
              <Box sx={{ justifySelf: "end" }}>
                <Tooltip title="Open options">
                  <IconButton onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ color: "#2196f3" }}>
                    <LoginIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Tooltip>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                  {sessions.map((session) => (
                    <MenuItem key={session.title} onClick={() => setAnchorEl(null)} component={Link} to={session.href}>
                      {session.title}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ) : (
              <Box sx={{ justifySelf: "end" }}>
                <Tooltip title="Open settings">
                  <IconButton onClick={(event) => setAnchorElUser(event.currentTarget)} sx={{ p: 0 }}>
                    <Avatar alt="User avatar" src={avatar || "https://i.pinimg.com/736x/49/c6/f1/49c6f19b6c8033d8a83e899d11719f07.jpg"} />
                  </IconButton>
                </Tooltip>
                <Menu anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={() => setAnchorElUser(null)}>
                  {settings.map((setting) => (
                    <MenuItem key={setting.title} onClick={() => setAnchorElUser(null)} component={Link} to={setting.href}>
                      <Typography onClick={setting.onClick}>{setting.title}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      <Container sx={{ maxWidth: "1240px !important", width: isMobile ? "94vw" : isTablet ? "86vw" : "100%", pt: 4 }}>
        <Paper elevation={0} sx={{ p: isMobile ? 2.5 : 3, mb: 3, borderRadius: 3, border: "1px solid #e5edf5", bgcolor: "#ffffff" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 2, alignItems: "center" }}>
            <Box>
              <Chip label="Completed review" sx={{ bgcolor: "#ffe8f0", color: "#d81b60", mb: 1.5 }} />
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ color: "#07111f", fontWeight: 700 }}>
                {name || "TOEIC Practice Test"}
              </Typography>
              <Typography sx={{ mt: 1, color: "#5f6b7a" }}>
                Review your selected answers against the correct answer key.
              </Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1, minWidth: isMobile ? "100%" : 320 }}>
              {[
                ["Correct", correctCount, "#1f9b45", "#e8f9ed"],
                ["Wrong", wrongCount, "#d93025", "#fff1f1"],
                ["Score", totalScore, "#6d4aff", "#f0ecff"],
              ].map(([label, value, color, bg]) => (
                <Box key={label} sx={{ p: 1.25, borderRadius: 2, bgcolor: bg, border: "1px solid #e5edf5", textAlign: "center" }}>
                  <Typography sx={{ color, fontWeight: 700, fontSize: 22 }}>{value}</Typography>
                  <Typography sx={{ color: "#5f6b7a", fontSize: 12, fontWeight: 500 }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, md: 1 },
            mb: { xs: 2, md: 0 },
            borderRadius: "14px",
            border: "1px solid rgba(185, 220, 255, 0.72)",
            bgcolor: "var(--toeic-paper-grid)",
            boxShadow: "var(--toeic-shadow-card)",
            position: { xs: "sticky", md: "fixed" },
            top: { xs: 84, md: "50%" },
            right: { md: 16 },
            transform: { md: "translateY(-50%)" },
            width: { xs: "auto", md: 116 },
            zIndex: 30,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "column" },
              alignItems: "stretch",
              gap: 1,
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            <Chip
              size="small"
              label={isMobile ? "Jump to part" : "Parts"}
              sx={{
                borderRadius: "10px",
                bgcolor: "rgba(70, 168, 232, 0.12)",
                color: "var(--toeic-sky-deep)",
                fontWeight: 700,
              }}
            />
            {QUESTION_GROUPS.map((group, index) => {
              const isListening = group.title.includes("Listening");

              return (
                <Button
                  key={group.title}
                  size="small"
                  onClick={() => handleJumpToPart(index)}
                  sx={{
                    minWidth: { xs: 70, md: "100%" },
                    minHeight: 32,
                    borderRadius: "10px",
                    border: `1px solid ${isListening ? "rgba(70, 168, 232, 0.26)" : "rgba(79, 191, 159, 0.28)"}`,
                    bgcolor: isListening ? "#edf7ff" : "#e8f9ed",
                    color: isListening ? "#2196f3" : "#1f9b45",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: isListening ? "#dceeff" : "#d8f5e0",
                      boxShadow: "0 8px 18px rgba(35, 135, 204, 0.1)",
                    },
                  }}
                >
                  Part {index + 1}
                </Button>
              );
            })}
          </Box>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 300px", gap: 3, alignItems: "start" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 2 }}>
            {QUESTION_GROUPS.map((group, index) => {
              const Icon = group.icon;
              const isListening = group.title.includes("Listening");

              return (
                <Paper elevation={0} key={group.title} id={`review-part-${index + 1}`} sx={{ p: isMobile ? 1.5 : 2, borderRadius: 3, border: "1px solid #e5edf5", bgcolor: "#ffffff", scrollMarginTop: 150 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: isListening ? "#edf7ff" : "#e8f9ed", color: isListening ? "#2196f3" : "#35c759" }}>
                      <Icon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography sx={{ color: "#07111f", fontWeight: 700 }}>{group.title}</Typography>
                      <Typography sx={{ color: "#5f6b7a", fontSize: 13 }}>{group.range}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "grid", gap: 1 }}>{renderQuestions(group.start, group.end)}</Box>
                </Paper>
              );
            })}
          </Box>

          <Paper elevation={0} sx={{ position: isMobile ? "static" : "sticky", top: 92, p: 2, borderRadius: 3, border: "1px solid #e5edf5", bgcolor: "#ffffff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Result</Typography>
            <ScoreCircle score={totalScore} />
            <Box sx={{ mt: 2, display: "grid", gap: 1, width: "100%" }}>
              {[
                ["Listening", `${listeningTrueAnswers}/100`, listeningScore, HeadphonesIcon, "#2196f3", "#edf7ff", "#b9dcff"],
                ["Reading", `${readingTrueAnswers}/100`, readingScore, MenuBookIcon, "#35c759", "#e8f9ed", "#a9eabc"],
              ].map(([label, correct, score, Icon, color, bg, border]) => (
                <Box key={label} sx={{ p: 1.5, borderRadius: 2, bgcolor: bg, border: `1px solid ${border}`, textAlign: "left", display: "grid", gridTemplateColumns: "40px 1fr", gap: 1.25, alignItems: "center" }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "#ffffff", color }}>
                    <Icon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: "#07111f", fontWeight: 700 }}>{label}</Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
                      <Typography sx={{ color: "#5f6b7a", fontSize: 13, fontWeight: 500 }}>{correct} correct</Typography>
                      <Typography sx={{ color, fontWeight: 700, fontSize: 20 }}>{score} pts</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            <Button fullWidth sx={{ mt: 2, bgcolor: "#35c759", color: "#ffffff", "&:hover": { bgcolor: "#2fb44f" } }} variant="contained" startIcon={<PrintIcon />} onClick={handlePrintResult}>
              Print result
            </Button>
          </Paper>
        </Box>

        <Paper elevation={0} sx={{ mt: 4, p: isMobile ? 2 : 3, borderRadius: 3, border: "1px solid #e5edf5", bgcolor: "#ffffff" }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Comments</Typography>
          <CommentSection answerID={answerID} />
        </Paper>
      </Container>
    </Box>
  );
}

export default Review;
